import Vue from 'vue'
import { first } from 'lodash-es'
import { dmsService } from '@/services/api/dms'
import { TOAST_TYPES, STATUS_CODES } from '@/constants'

function getInitialState () {
    return {
        isLoading: false
    }
}

const state = getInitialState()

const actions = {
    async getFilesAndFoldersAtPath (
        { commit, rootGetters, dispatch },
        { path }
    ) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            const companyId = rootGetters['company/company'].id
            return await dmsService.getFilesAndFoldersAtPath(companyId, path)
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async deleteFileDms (
        { commit, rootGetters },
        { documentId, documentType }
    ) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.deleteFileFromDms(
                rootGetters['company/company'].id,
                documentId,
                documentType
            )
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async deleteFolderDms (
        { commit, rootGetters, dispatch },
        { folderId, path }
    ) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.deleteFolderFromDms(
                rootGetters['company/company'].id,
                folderId,
                path
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
            if (error.response.status === STATUS_CODES.VALIDATION_FAILED) {
                Vue.toasted.show(first(error.response.data.errors), {
                    type: TOAST_TYPES.ERROR
                })
                dispatch('errors/clearAllErrors', null, { root: true })
            }
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async setPermissions ({ commit, rootGetters }, { folderId, data }) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.setPermissions(
                rootGetters['company/company'].id,
                folderId,
                data
            )
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async createFolderDms ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.createFolderToDms(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
            throw error
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async renameFile ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            // BE shape (CT-378 sync_warnings wrapper): { document, sync_warnings: [{type, reason}] }.
            // sync_warnings is empty on a fully-successful rename. Non-empty entries mean the
            // local rename succeeded but the external mirror (SharePoint / Dropbox) diverged —
            // surface them as persistent toasts so the user knows their files need attention.
            // Callers receive the unwrapped Document, matching the old contract.
            const response = await dmsService.renameFile(
                rootGetters['company/company'].id,
                data
            )
            const warnings = response?.sync_warnings ?? []
            if (warnings.length > 0) {
                dispatch('integrations/showSyncWarnings', warnings, { root: true })
            }
            return response?.document ?? response
        } catch (error) {
            // 409 with structured error_code = 'filename_collision' means the caller component
            // (FileAndDirectoryExplorer) will render a collision modal with rename / delete
            // options. Skip the generic toast to avoid a duplicate error surface — same pattern
            // CT-378 uses for SharePoint structured 422 errors. The error still re-throws so the
            // caller knows the rename did not commit.
            const errorCode = error?.response?.data?.error_code
            if (errorCode !== 'filename_collision') {
                dispatch('errors/handleError', error, { root: true })
            }
            throw error
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async renameFolder ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.renameFolder(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
            throw error
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async moveFile ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.moveFile(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async moveFolder ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.moveFolder(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async shareFile ({ commit, rootGetters, dispatch }, data) {
        try {
            return await dmsService.shareFile(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
            throw error
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async copyFile ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.copyFile(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async copyFolder ({ commit, rootGetters, dispatch }, data) {
        commit('SET_APP_LOADING', true, { root: true })
        try {
            return await dmsService.copyFolder(
                rootGetters['company/company'].id,
                data
            )
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
        } finally {
            commit('SET_APP_LOADING', false, { root: true })
        }
    },

    async getDocumentVersions (
        { commit, rootGetters, dispatch },
        { documentId }
    ) {
        commit('setIsLoading', true)
        try {
            const companyId = rootGetters['company/company'].id
            const response = await dmsService.getDocumentVersions(
                companyId,
                documentId
            )

            const versions = Array.isArray(response?.data) ? response.data : []

            return versions
        } catch (error) {
            dispatch('errors/handleError', error, { root: true })
            throw error
        } finally {
            commit('setIsLoading', false)
        }
    }
}

const mutations = {
    setIsLoading (state, isLoading) {
        state.isLoading = isLoading
    }
}

export const dms = {
    namespaced: true,
    state,
    actions,
    mutations
}
